let ctx: AudioContext | null = null;

function ac(): AudioContext | null {
  try {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function tone(
  freq: number,
  duration: number,
  type: OscillatorType = 'sine',
  gain = 0.25,
  startTime?: number,
): void {
  const c = ac();
  if (!c) return;
  const t = startTime ?? c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(t);
  osc.stop(t + duration + 0.01);
}

export function playPellet(): void {
  tone(880, 0.05, 'sine', 0.12);
}

export function playEat(): void {
  tone(440, 0.08, 'sine', 0.18);
}

export function playBrick(): void {
  tone(260, 0.07, 'square', 0.12);
}

export function playPaddle(): void {
  tone(330, 0.04, 'sine', 0.1);
}

export function playBallOut(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  tone(300, 0.05, 'sine', 0.15, now);
  tone(200, 0.12, 'sine', 0.2, now + 0.05);
}

export function playGolden(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  tone(400, 0.08, 'sine', 0.2, now);
  tone(600, 0.08, 'sine', 0.2, now + 0.08);
  tone(900, 0.15, 'sine', 0.28, now + 0.16);
}

export function playDive(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  tone(300, 0.07, 'sine', 0.18, now);
  tone(450, 0.07, 'sine', 0.18, now + 0.07);
  tone(675, 0.07, 'sine', 0.18, now + 0.14);
  tone(1000, 0.18, 'sine', 0.25, now + 0.21);
}

export function playAscend(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  tone(1000, 0.07, 'sine', 0.2, now);
  tone(675, 0.07, 'sine', 0.2, now + 0.07);
  tone(450, 0.12, 'sine', 0.22, now + 0.14);
}

export function playGameStart(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  [261, 329, 392, 523].forEach((f, i) => tone(f, 0.1, 'sine', 0.18, now + i * 0.1));
}

export function playGameOver(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  [440, 370, 300, 220].forEach((f, i) => tone(f, 0.15, 'sine', 0.25, now + i * 0.13));
}

export function playReactionPrompt(): void {
  tone(660, 0.06, 'sine', 0.28);
}

export function playReactionSuccess(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  tone(500, 0.07, 'sine', 0.18, now);
  tone(750, 0.1, 'sine', 0.22, now + 0.07);
}

export function playReactionMiss(): void {
  tone(140, 0.22, 'sawtooth', 0.18);
}

export function playReactionDone(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  [400, 500, 600, 750, 900].forEach((f, i) => tone(f, 0.1, 'sine', 0.2, now + i * 0.08));
}

export function playPowerUp(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  // Quick ascending sparkle: three rising tones
  [600, 800, 1100].forEach((f, i) => tone(f, 0.12, 'sine', 0.18, now + i * 0.06));
}

export function playGhostCatch(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  // Descending alarm: sawtooth drops from 880 → 220 Hz
  [880, 660, 440, 220].forEach((f, i) => tone(f, 0.1, 'sawtooth', 0.3, now + i * 0.1));
}

export function playGameWin(): void {
  const c = ac();
  if (!c) return;
  const now = c.currentTime;
  // Ascending C-major scale
  [261, 329, 392, 523, 659, 784, 1047].forEach((f, i) => tone(f, 0.18, 'sine', 0.2, now + i * 0.09));
  // Final triumphant chord
  const chordStart = now + 7 * 0.09 + 0.05;
  [523, 659, 784, 1047].forEach((f) => tone(f, 0.8, 'sine', 0.15, chordStart));
}
